import React, { useState, useEffect } from 'react';
import { 
  Plus, Pencil, Trash2, Eye, EyeOff, Calendar, MapPin, 
  Users, Loader2, PoundSterling, BedDouble,
  UserPlus, Minus, User, FileText, CreditCard, RefreshCw, Receipt, Mail, AlertCircle, Edit, MoreHorizontal, Play, Pause, X, Ban, LayoutDashboard
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Checkbox } from '../ui/checkbox';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter 
} from '../ui/dialog';
import { 
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../ui/alert-dialog';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger
} from '../ui/dropdown-menu';
import { toast } from 'sonner';
import { API_URL } from '../../utils/api';
import DancerPaymentShareManager from './DancerPaymentShareManager';
import { WeekenderWorkspace, ManualWeekenderBookingDialog, EditWeekenderBookingDialog, RecordWeekenderPaymentDialog, WeekenderInvoiceDialog, CreateWeekenderDialog } from './weekender';

export function AdminWeekendsTab({ searchQuery, isBookingManager = false }) {
  const [events, setEvents] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [paymentPlans, setPaymentPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [showBookingsDialog, setShowBookingsDialog] = useState(false);
  const [bookingSearchTerm, setBookingSearchTerm] = useState('');
  const [showManualBookingDialog, setShowManualBookingDialog] = useState(false);
  const [showEditBookingDialog, setShowEditBookingDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  // showTestEmailDialog state removed - Test Email moved to Email Templates tab (Phase 3J)
  const [showPaymentPlansDialog, setShowPaymentPlansDialog] = useState(false);
  // showSummaryDialog, summaryData, showSetRoomCountsDialog, roomCountsForm, loadingSummary 
  // removed in Phase 3K - functionality moved to WeekenderRooms (Set Manually) and WeekenderOverview (Print)
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null); // For Create/Edit Event dialog
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteBookingConfirm, setDeleteBookingConfirm] = useState(null);
  const [cancelBookingConfirm, setCancelBookingConfirm] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Weekender Workspace state
  const [workspaceEvent, setWorkspaceEvent] = useState(null);
  const [authToken, setAuthToken] = useState(null);
 
 // Edit dancer share state
 const [editingDancerShare, setEditingDancerShare] = useState(null);
 const [editDancerShareForm, setEditDancerShareForm] = useState({
 monthly_payment:'',
 share_amount:'',
 dancer_name:'',
 dancer_email:''
 });

 // Edit regular payment plan state
 const [editingPaymentPlan, setEditingPaymentPlan] = useState(null);
 const [editPaymentPlanForm, setEditPaymentPlanForm] = useState({
 monthly_amount:'',
 remaining_balance:''
 });

 // Add Card for Dancer Share state
 const [addingCardForShare, setAddingCardForShare] = useState(null);
 const [squareCard, setSquareCard] = useState(null);
 const [squareLoaded, setSquareLoaded] = useState(false);
 const [addingCardProcessing, setAddingCardProcessing] = useState(false);
 const [squareAppId, setSquareAppId] = useState(null);
 const [squareLocationId, setSquareLocationId] = useState(null);
 
 // Process payment confirmation dialog state
 const [processConfirmPlan, setProcessConfirmPlan] = useState(null);
 const [deletingPlanId, setDeletingPlanId] = useState(null);
 
 // Event Create/Edit formData state removed - now managed inside CreateWeekenderDialog component

 // Manual booking form state
 const [bookingForm, setBookingForm] = useState({
 customer_name:'',
 customer_email:'',
 customer_phone:'',
 rooms: {},
 tickets: {},
 guests: [],
 selected_add_ons: [],
 special_requests:'',
 status:'confirmed',
 amount_paid: 0
 });

 // Legacy Edit/Payment/Invoice states removed - now managed inside extracted components

 useEffect(() => {
 fetchEvents();
 // Get auth token for workspace
 const token = localStorage.getItem('token');
 setAuthToken(token);
 }, []);

 const fetchEvents = async () => {
 setLoading(true);
 try {
 const token = localStorage.getItem('token');
 const response = await fetch(`${API_URL}/api/weekends/admin/events`, {
 headers: {'Authorization': `Bearer ${token}` }
 });
 if (response.ok) {
 const data = await response.json();
 // Sort events by start_date, earliest first
 const sortedData = data.sort((a, b) => {
 const dateA = new Date(a.start_date);
 const dateB = new Date(b.start_date);
 return dateA - dateB;
 });
 setEvents(sortedData);
 }
 } catch (error) {
 console.error('Error fetching events:', error);
 toast.error('Failed to load weekend events');
 } finally {
 setLoading(false);
 }
 };

 const fetchBookings = async (eventId) => {
 try {
 const token = localStorage.getItem('token');
 const response = await fetch(`${API_URL}/api/weekends/admin/bookings?event_id=${eventId}`, {
 headers: {'Authorization': `Bearer ${token}` }
 });
 if (response.ok) {
 const data = await response.json();
 setBookings(data);
 setShowBookingsDialog(true);
 }
 } catch (error) {
 toast.error('Failed to load bookings');
 }
 };

 // fetchSummary removed in Phase 3K - Summary view is now in WeekenderOverview with SummaryPrintExport

 const fetchPaymentPlans = async () => {
 try {
 const token = localStorage.getItem('token');
 console.log('[PaymentPlans] Fetching with token:', token ?'present' :'missing');
 const response = await fetch(`${API_URL}/api/weekends/admin/payment-plans`, {
 headers: {'Authorization': `Bearer ${token}` }
 });
 console.log('[PaymentPlans] Response status:', response.status);
 if (response.ok) {
 const data = await response.json();
 console.log('[PaymentPlans] Received data:', data);
 console.log('[PaymentPlans] Plans count:', data.plans?.length || 0);
 setPaymentPlans(data.plans || []);
 } else {
 const errorText = await response.text();
 console.error('[PaymentPlans] Error response:', response.status, errorText);
 toast.error(`Failed to load payment plans: ${response.status}`);
 setPaymentPlans([]);
 }
 } catch (error) {
 console.error('[PaymentPlans] Fetch error:', error);
 toast.error('Failed to load payment plans:' + error.message);
 setPaymentPlans([]);
 }
 // Always open the dialog, even if fetch fails
 setShowPaymentPlansDialog(true);
 };

 const handlePauseResumePlan = async (planId, currentStatus) => {
 const action = currentStatus ==='active' ?'pause' :'resume';
 try {
 const token = localStorage.getItem('token');
 const response = await fetch(`${API_URL}/api/weekends/admin/payment-plans/${planId}/${action}`, {
 method:'POST',
 headers: {'Authorization': `Bearer ${token}` }
 });
 if (response.ok) {
 toast.success(`Payment plan ${action}d`);
 fetchPaymentPlans(); // Refresh
 } else {
 const data = await response.json();
 toast.error(data.detail || `Failed to ${action} plan`);
 }
 } catch (error) {
 toast.error(`Failed to ${action} payment plan`);
 }
 };

 const handleCancelPlan = async (planId) => {
 if (!window.confirm('Are you sure you want to cancel this payment plan?')) return;
 try {
 const token = localStorage.getItem('token');
 const response = await fetch(`${API_URL}/api/weekends/admin/payment-plans/${planId}/cancel`, {
 method:'POST',
 headers: {'Authorization': `Bearer ${token}` }
 });
 if (response.ok) {
 toast.success('Payment plan cancelled');
 fetchPaymentPlans(); // Refresh
 } else {
 const data = await response.json();
 toast.error(data.detail ||'Failed to cancel plan');
 }
 } catch (error) {
 toast.error('Failed to cancel payment plan');
 }
 };

 const handleSetEndDate = async (planId, endDate) => {
 try {
 const token = localStorage.getItem('token');
 const response = await fetch(`${API_URL}/api/weekends/admin/payment-plans/${planId}/set-end-date`, {
 method:'POST',
 headers: { 
'Authorization': `Bearer ${token}`,
'Content-Type':'application/json'
 },
 body: JSON.stringify({ end_date: endDate })
 });
 if (response.ok) {
 toast.success(endDate ?'End date set' :'End date cleared');
 fetchPaymentPlans(); // Refresh
 } else {
 const data = await response.json();
 toast.error(data.detail ||'Failed to set end date');
 }
 } catch (error) {
 toast.error('Failed to set end date');
 }
 };

 // Dancer Share handlers
 const handlePauseResumeDancerShare = async (shareId, currentStatus) => {
 const action = currentStatus ==='active' ?'pause' :'resume';
 try {
 const token = localStorage.getItem('token');
 const response = await fetch(`${API_URL}/api/weekends/admin/dancer-shares/${shareId}/${action}`, {
 method:'POST',
 headers: {'Authorization': `Bearer ${token}` }
 });
 if (response.ok) {
 toast.success(`Dancer share ${action}d`);
 fetchPaymentPlans(); // Refresh
 } else {
 const data = await response.json();
 toast.error(data.detail || `Failed to ${action} dancer share`);
 }
 } catch (error) {
 toast.error(`Failed to ${action} dancer share`);
 }
 };

 const [processingShareId, setProcessingShareId] = useState(null);
 
 const handleProcessDancerShare = async (shareId) => {
 // Prevent double-clicks
 if (processingShareId) return;
 
 setProcessingShareId(shareId);
 try {
 const token = localStorage.getItem('token');
 console.log('[ProcessDancerShare] Processing share:', shareId);
 
 // Use XMLHttpRequest to avoid response body being consumed by platform scripts
 const result = await new Promise((resolve, reject) => {
 const xhr = new XMLHttpRequest();
 xhr.open('POST', `${API_URL}/api/weekends/admin/dancer-shares/${shareId}/process`);
 xhr.setRequestHeader('Authorization', `Bearer ${token}`);
 xhr.setRequestHeader('Content-Type','application/json');
 xhr.onload = function() {
 try {
 const data = JSON.parse(xhr.responseText);
 resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data });
 } catch (e) {
 resolve({ ok: false, status: xhr.status, data: { detail: xhr.responseText } });
 }
 };
 xhr.onerror = function() {
 reject(new Error('Network request failed'));
 };
 xhr.send();
 });
 
 console.log('[ProcessDancerShare] Response:', result);
 
 if (result.ok) {
 toast.success(result.data.message ||'Payment processed successfully');
 fetchPaymentPlans(); // Refresh
 } else {
 toast.error(result.data.detail ||'Failed to process payment');
 }
 } catch (error) {
 console.error('[ProcessDancerShare] Error:', error);
 toast.error('Network error:' + error.message);
 } finally {
 setProcessingShareId(null);
 setProcessConfirmPlan(null);
 }
 };

 const handleResendPaymentLink = async (shareId, dancerEmail) => {
 if (!window.confirm(`Resend payment link to ${dancerEmail}?`)) return;
 try {
 const token = localStorage.getItem('token');
 const response = await fetch(`${API_URL}/api/dancer-payments/admin/shares/${shareId}/resend-link`, {
 method:'POST',
 headers: {'Authorization': `Bearer ${token}` }
 });
 const data = await response.json();
 if (response.ok) {
 toast.success(data.message ||'Payment link sent');
 } else {
 toast.error(data.detail ||'Failed to send payment link');
 }
 } catch (error) {
 toast.error('Failed to send payment link');
 }
 };

 const handleProcessSinglePlan = async (planId) => {
 try {
 const token = localStorage.getItem('token');
 const response = await fetch(`${API_URL}/api/weekends/admin/payment-plans/${planId}/process`, {
 method:'POST',
 headers: {'Authorization': `Bearer ${token}` }
 });
 const data = await response.json();
 if (response.ok) {
 toast.success(data.message ||'Payment processed successfully');
 fetchPaymentPlans(); // Refresh
 } else {
 toast.error(data.detail ||'Failed to process payment');
 }
 } catch (error) {
 toast.error('Failed to process payment plan');
 } finally {
 setProcessConfirmPlan(null);
 }
 };

 // Delete cancelled payment plan
 const handleDeleteCancelledPlan = async (planId, planType) => {
 setDeletingPlanId(planId);
 try {
 const token = localStorage.getItem('token');
 const endpoint = planType ==='dancer_share' 
 ? `${API_URL}/api/weekends/admin/dancer-shares/${planId}`
 : `${API_URL}/api/weekends/admin/payment-plans/${planId}`;
 
 const response = await fetch(endpoint, {
 method:'DELETE',
 headers: {'Authorization': `Bearer ${token}` }
 });
 
 if (response.ok) {
 toast.success('Plan deleted successfully');
 fetchPaymentPlans(); // Refresh
 } else {
 const data = await response.json();
 toast.error(data.detail ||'Failed to delete plan');
 }
 } catch (error) {
 toast.error('Failed to delete plan');
 } finally {
 setDeletingPlanId(null);
 }
 };

 // Open edit modal for dancer share
 const handleEditDancerShare = (share) => {
 setEditingDancerShare(share);
 setEditDancerShareForm({
 monthly_payment: share.monthly_amount || share.monthly_payment ||'',
 share_amount: share.booking_total || share.share_amount ||'',
 dancer_name: share.customer_name || share.dancer_name ||'',
 dancer_email: share.customer_email || share.dancer_email ||''
 });
 };

 // Save edited dancer share
 const handleSaveDancerShare = async () => {
 if (!editingDancerShare) return;
 
 try {
 const token = localStorage.getItem('token');
 const shareId = editingDancerShare.plan_id || editingDancerShare.share_id;
 
 const response = await fetch(`${API_URL}/api/weekends/admin/dancer-shares/${shareId}/update`, {
 method:'POST',
 headers: {
'Content-Type':'application/json',
'Authorization': `Bearer ${token}`
 },
 body: JSON.stringify({
 monthly_payment: parseFloat(editDancerShareForm.monthly_payment) || 0,
 share_amount: parseFloat(editDancerShareForm.share_amount) || 0,
 dancer_name: editDancerShareForm.dancer_name,
 dancer_email: editDancerShareForm.dancer_email
 })
 });
 
 const data = await response.json();
 if (response.ok) {
 toast.success('Dancer share updated successfully');
 setEditingDancerShare(null);
 fetchPaymentPlans(); // Refresh
 } else {
 toast.error(data.detail ||'Failed to update dancer share');
 }
 } catch (error) {
 toast.error('Failed to update dancer share');
 }
 };

 // Open edit modal for regular payment plan
 const handleEditPaymentPlan = (plan) => {
 setEditingPaymentPlan(plan);
 setEditPaymentPlanForm({
 monthly_amount: plan.monthly_amount ||'',
 remaining_balance: plan.remaining_balance ||''
 });
 };

 // Save edited payment plan
 const handleSavePaymentPlan = async () => {
 if (!editingPaymentPlan) return;
 
 try {
 const token = localStorage.getItem('token');
 const planId = editingPaymentPlan.plan_id;
 
 const response = await fetch(`${API_URL}/api/weekends/admin/payment-plans/${planId}/update`, {
 method:'POST',
 headers: {
'Content-Type':'application/json',
'Authorization': `Bearer ${token}`
 },
 body: JSON.stringify({
 monthly_amount: parseFloat(editPaymentPlanForm.monthly_amount) || 0,
 remaining_balance: parseFloat(editPaymentPlanForm.remaining_balance) || 0
 })
 });
 
 const data = await response.json();
 if (response.ok) {
 toast.success('Payment plan updated successfully');
 setEditingPaymentPlan(null);
 fetchPaymentPlans(); // Refresh
 } else {
 toast.error(data.detail ||'Failed to update payment plan');
 }
 } catch (error) {
 toast.error('Failed to update payment plan');
 }
 };

 // Open Add Card dialog for a dancer share
 const handleOpenAddCard = async (share) => {
 setAddingCardForShare(share);
 setSquareCard(null);
 
 // Fetch Square credentials
 try {
 const token = localStorage.getItem('token');
 const response = await fetch(`${API_URL}/api/dancer-payments/admin/square-credentials`, {
 headers: {'Authorization': `Bearer ${token}` }
 });
 if (response.ok) {
 const data = await response.json();
 setSquareAppId(data.app_id);
 setSquareLocationId(data.location_id);
 
 // Load Square SDK if not already loaded
 if (!window.Square) {
 const script = document.createElement('script');
 script.src ='https://web.squarecdn.com/v1/square.js';
 script.async = true;
 script.onload = () => setSquareLoaded(true);
 document.body.appendChild(script);
 } else {
 setSquareLoaded(true);
 }
 } else {
 toast.error('Failed to load payment credentials');
 setAddingCardForShare(null);
 }
 } catch (error) {
 toast.error('Failed to initialize payment form');
 setAddingCardForShare(null);
 }
 };

 // Initialize Square card when SDK loads and dialog opens
 useEffect(() => {
 if (!squareLoaded || !addingCardForShare || !squareAppId || !squareLocationId) return;
 
 const initCard = async () => {
 try {
 // Wait for container to be in DOM
 await new Promise(resolve => setTimeout(resolve, 100));
 
 const container = document.getElementById('admin-card-container');
 if (!container) {
 console.error('Card container not found');
 return;
 }
 
 const payments = window.Square.payments(squareAppId, squareLocationId);
 const card = await payments.card();
 await card.attach('#admin-card-container');
 setSquareCard(card);
 } catch (err) {
 console.error('Failed to init Square card:', err);
 toast.error('Failed to initialize card form');
 }
 };
 
 initCard();
 }, [squareLoaded, addingCardForShare, squareAppId, squareLocationId]);

 // Add card for dancer share
 const handleAddCardForShare = async () => {
 if (!squareCard || !addingCardForShare) return;
 
 setAddingCardProcessing(true);
 try {
 console.log('[AddCard] Starting tokenization...');
 const tokenResult = await squareCard.tokenize();
 console.log('[AddCard] Tokenization result:', tokenResult.status);
 
 if (tokenResult.status !=='OK') {
 console.error('[AddCard] Tokenization failed:', tokenResult.errors);
 toast.error(tokenResult.errors?.[0]?.message ||'Card verification failed');
 setAddingCardProcessing(false);
 return;
 }
 
 const token = localStorage.getItem('token');
 const shareId = addingCardForShare.plan_id || addingCardForShare.share_id;
 console.log('[AddCard] Sending to backend, shareId:', shareId);
 
 // Use XMLHttpRequest to avoid body stream issues
 const result = await new Promise((resolve, reject) => {
 const xhr = new XMLHttpRequest();
 xhr.open('POST', `${API_URL}/api/dancer-payments/admin/shares/${shareId}/add-card`);
 xhr.setRequestHeader('Content-Type','application/json');
 xhr.setRequestHeader('Authorization', `Bearer ${token}`);
 xhr.onload = function() {
 let data = {};
 try {
 data = JSON.parse(xhr.responseText);
 } catch (e) {
 data = { detail: xhr.responseText };
 }
 resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data });
 };
 xhr.onerror = function() {
 reject(new Error('Network request failed'));
 };
 xhr.send(JSON.stringify({
 card_nonce: tokenResult.token,
 verification_token: tokenResult.verificationToken
 }));
 });
 
 console.log('[AddCard] Backend response:', result);
 
 if (result.ok) {
 toast.success(result.data.message ||'Card added successfully');
 setAddingCardForShare(null);
 setSquareCard(null);
 fetchPaymentPlans();
 } else {
 toast.error(result.data.detail ||'Failed to add card');
 }
 } catch (error) {
 console.error('[AddCard] Error:', error);
 toast.error('Failed to add card:' + error.message);
 } finally {
 setAddingCardProcessing(false);
 }
 };

 // handleSubmit for event creation/edit removed - now managed inside CreateWeekenderDialog component

 const handleDelete = async (eventId) => {
 try {
 const token = localStorage.getItem('token');
 const response = await fetch(`${API_URL}/api/weekends/admin/events/${eventId}`, {
 method:'DELETE',
 headers: {'Authorization': `Bearer ${token}` }
 });

 if (response.ok) {
 toast.success('Event deleted');
 fetchEvents();
 } else {
 const error = await response.json();
 toast.error(error.detail ||'Failed to delete event');
 }
 } catch (error) {
 toast.error('Failed to delete event');
 }
 setDeleteConfirm(null);
 };

 const togglePublished = async (event) => {
 try {
 const token = localStorage.getItem('token');
 const response = await fetch(`${API_URL}/api/weekends/admin/events/${event.event_id}`, {
 method:'PUT',
 headers: {
'Content-Type':'application/json',
'Authorization': `Bearer ${token}`
 },
 body: JSON.stringify({ is_published: !event.is_published })
 });

 if (response.ok) {
 toast.success(event.is_published ?'Event unpublished' :'Event published!');
 fetchEvents();
 }
 } catch (error) {
 toast.error('Failed to update event');
 }
 };

 // resetForm, openEditDialog, and room/ticket/add-on helper functions removed - 
 // now managed inside CreateWeekenderDialog component

 const formatDate = (dateStr) => {
 if (!dateStr) return'N/A';
 return new Date(dateStr).toLocaleDateString('en-GB', {
 day:'numeric',
 month:'short',
 year:'numeric'
 });
 };

 // ============== MANUAL BOOKING HELPERS ==============

 const getRoomOccupancy = (roomType) => {
 const type = roomType?.toLowerCase() ||'';
 if (type ==='single') return 1;
 if (type ==='double' || type ==='twin') return 2;
 if (type ==='triple') return 3;
 if (type ==='quad' || type ==='family') return 4;
 return 1;
 };

 const getTotalGuests = () => {
 if (!selectedEvent) return 0;
 let total = 0;
 Object.entries(bookingForm.rooms).forEach(([roomType, count]) => {
 total += count * getRoomOccupancy(roomType);
 });
 return total;
 };

 const updateBookingRoomQuantity = (roomType, delta) => {
 setBookingForm(prev => {
 const currentQty = prev.rooms[roomType] || 0;
 const newQty = Math.max(0, currentQty + delta);
 const newRooms = { ...prev.rooms };
 
 if (newQty === 0) {
 delete newRooms[roomType];
 } else {
 newRooms[roomType] = newQty;
 }
 
 return { ...prev, rooms: newRooms };
 });
 };

 // Initialize guests array when rooms change
 useEffect(() => {
 const totalGuests = getTotalGuests();
 setBookingForm(prev => {
 const newGuests = [...prev.guests];
 while (newGuests.length < totalGuests) {
 newGuests.push({ first_name:'', last_name:'', email:'' });
 }
 while (newGuests.length > totalGuests) {
 newGuests.pop();
 }
 return { ...prev, guests: newGuests };
 });
 }, [bookingForm.rooms, selectedEvent]);

 const updateBookingGuestName = (index, field, value) => {
 setBookingForm(prev => {
 const newGuests = [...prev.guests];
 if (!newGuests[index]) {
 newGuests[index] = { first_name:'', last_name:'', email:'' };
 }
 newGuests[index][field] = value;
 return { ...prev, guests: newGuests };
 });
 };

 const toggleBookingAddOn = (addOnName) => {
 setBookingForm(prev => ({
 ...prev,
 selected_add_ons: prev.selected_add_ons.includes(addOnName)
 ? prev.selected_add_ons.filter(a => a !== addOnName)
 : [...prev.selected_add_ons, addOnName]
 }));
 };

 const calculateBookingTotals = () => {
 if (!selectedEvent) return { rooms: 0, addOns: 0, subtotal: 0, deposit: 0 };
 
 const totalGuests = getTotalGuests();
 let roomsTotal = 0;
 
 Object.entries(bookingForm.rooms).forEach(([roomType, count]) => {
 const roomOption = selectedEvent.room_options?.find(r => r.type === roomType);
 if (roomOption) {
 const occupancy = getRoomOccupancy(roomType);
 const guestsInRoomType = count * occupancy;
 roomsTotal += (roomOption.price_per_person || 0) * guestsInRoomType;
 }
 });
 
 let addOnsTotal = 0;
 bookingForm.selected_add_ons.forEach(addOnName => {
 const addOn = selectedEvent.add_ons?.find(a => a.name === addOnName);
 if (addOn) addOnsTotal += addOn.price * totalGuests;
 });
 
 const subtotal = roomsTotal + addOnsTotal;
 const roomDeposit = (selectedEvent.room_deposit_amount || 50) * totalGuests;
 
 let addOnsDeposit = 0;
 bookingForm.selected_add_ons.forEach(addOnName => {
 const addOn = selectedEvent.add_ons?.find(a => a.name === addOnName);
 if (addOn?.deposit_amount) addOnsDeposit += addOn.deposit_amount * totalGuests;
 });
 
 return { rooms: roomsTotal, addOns: addOnsTotal, subtotal, deposit: roomDeposit + addOnsDeposit };
 };

 const resetBookingForm = () => {
 setBookingForm({
 customer_name:'',
 customer_email:'',
 customer_phone:'',
 rooms: {},
 guests: [],
 selected_add_ons: [],
 special_requests:'',
 status:'confirmed',
 amount_paid: 0
 });
 };

 const openManualBookingDialog = (event) => {
 setSelectedEvent(event);
 resetBookingForm();
 setShowManualBookingDialog(true);
 };

 const handleCreateManualBooking = async () => {
 const totalGuests = getTotalGuests();
 if (totalGuests === 0) {
 toast.error('Please select at least one room');
 return;
 }
 
 if (!bookingForm.customer_name || !bookingForm.customer_email || !bookingForm.customer_phone) {
 toast.error('Please fill in all customer details');
 return;
 }
 
 // Validate guest names
 const missingNames = bookingForm.guests.some(g => !g.first_name?.trim() || !g.last_name?.trim());
 if (missingNames) {
 toast.error('Please fill in all guest names');
 return;
 }
 
 setSaving(true);
 try {
 const token = localStorage.getItem('token');
 const roomsArray = Object.entries(bookingForm.rooms)
 .filter(([_, qty]) => qty > 0)
 .map(([room_type, quantity]) => ({ room_type, quantity }));
 
 const response = await fetch(`${API_URL}/api/weekends/admin/bookings`, {
 method:'POST',
 headers: {
'Content-Type':'application/json',
'Authorization': `Bearer ${token}`
 },
 body: JSON.stringify({
 event_id: selectedEvent.event_id,
 customer_name: bookingForm.customer_name,
 customer_email: bookingForm.customer_email,
 customer_phone: bookingForm.customer_phone,
 rooms: roomsArray,
 guests: bookingForm.guests.map(g => ({
 first_name: g.first_name,
 last_name: g.last_name,
 email: g.email && g.email.trim() ? g.email.trim() : null
 })),
 selected_add_ons: bookingForm.selected_add_ons,
 special_requests: bookingForm.special_requests,
 status: bookingForm.status,
 amount_paid: parseFloat(bookingForm.amount_paid) || 0
 })
 });

 if (response.ok) {
 toast.success('Manual booking created successfully');
 setShowManualBookingDialog(false);
 fetchEvents(); // Refresh to update room counts
 resetBookingForm();
 } else {
 const error = await response.json().catch(() => ({ detail: `Server error: ${response.status}` }));
 console.error('Create booking error:', error);
 toast.error(error.detail || `Failed to create booking (${response.status})`);
 }
 } catch (error) {
 console.error('Create booking exception:', error);
 toast.error(`Failed to create booking: ${error.message ||'Network error'}`);
 } finally {
 setSaving(false);
 }
 };

 // ============== LEGACY ENTRY POINTS - Now using extracted components ==============

 // Simple open functions that set the booking and open the dialog
 // All form state and logic is now handled inside the extracted components
 const openEditBookingDialog = (booking) => {
 setSelectedBooking(booking);
 setShowEditBookingDialog(true);
 };

 const openPaymentDialog = (booking) => {
 setSelectedBooking(booking);
 setShowPaymentDialog(true);
 };

 const openInvoiceDialog = (booking) => {
 setSelectedBooking(booking);
 setShowInvoiceDialog(true);
 };

 // Callback for when a booking operation succeeds - refreshes the booking list
 const handleBookingOperationSuccess = () => {
 if (selectedEvent) {
 fetchBookings(selectedEvent.event_id);
 }
 };

 // ============== RESEND CONFIRMATION EMAIL ==============
 
 const resendConfirmationEmail = async (booking) => {
 setSaving(true);
 try {
 const token = localStorage.getItem('token');
 const response = await fetch(`${API_URL}/api/weekends/admin/bookings/${booking.booking_id}/resend-confirmation`, {
 method:'POST',
 headers: { 
'Authorization': `Bearer ${token}`,
'Content-Type':'application/json'
 }
 });

 if (response.ok) {
 const result = await response.json();
 toast.success(`Confirmation email sent to ${booking.customer_email}`);
 } else {
 const error = await response.json();
 toast.error(error.detail ||'Failed to send email');
 }
 } catch (error) {
 toast.error('Failed to send confirmation email');
 } finally {
 setSaving(false);
 }
 };

 // ============== SEND PAYMENT RECEIPT ==============
 
 const sendPaymentReceipt = async (booking) => {
 if (!booking.payment_records || booking.payment_records.length === 0) {
 toast.error('No payments recorded for this booking');
 return;
 }
 
 setSaving(true);
 try {
 const token = localStorage.getItem('token');
 const response = await fetch(`${API_URL}/api/weekends/admin/bookings/${booking.booking_id}/send-receipt`, {
 method:'POST',
 headers: { 
'Authorization': `Bearer ${token}`,
'Content-Type':'application/json'
 }
 });

 if (response.ok) {
 const result = await response.json();
 toast.success(`Payment receipt sent to ${booking.customer_email}`);
 } else {
 const error = await response.json();
 toast.error(error.detail ||'Failed to send receipt');
 }
 } catch (error) {
 toast.error('Failed to send payment receipt');
 } finally {
 setSaving(false);
 }
 };

 // ============== DELETE BOOKING HANDLER ==============

 const handleDeleteBooking = async (bookingId) => {
 setSaving(true);
 try {
 const token = localStorage.getItem('token');
 const response = await fetch(`${API_URL}/api/weekends/admin/bookings/${bookingId}`, {
 method:'DELETE',
 headers: {'Authorization': `Bearer ${token}` }
 });

 if (response.ok) {
 toast.success('Booking deleted successfully');
 // Refresh bookings list
 if (selectedEvent) {
 fetchBookings(selectedEvent.event_id);
 }
 fetchEvents(); // Refresh to update counts
 } else {
 const error = await response.json();
 toast.error(error.detail ||'Failed to delete booking');
 }
 } catch (error) {
 toast.error('Failed to delete booking');
 } finally {
 setSaving(false);
 setDeleteBookingConfirm(null);
 }
 };

 // Cancel booking (keeps record, restores inventory, sends email)
 const handleCancelBooking = async (bookingId, sendEmail = true) => {
 setSaving(true);
 try {
 const token = localStorage.getItem('token');
 const response = await fetch(`${API_URL}/api/weekends/admin/bookings/${bookingId}/cancel?send_email=${sendEmail}`, {
 method: 'POST',
 headers: { 'Authorization': `Bearer ${token}` }
 });

 if (response.ok) {
 const result = await response.json();
 toast.success(result.email_sent ? 'Booking cancelled and customer notified' : 'Booking cancelled successfully');
 // Refresh bookings list
 if (selectedEvent) {
 fetchBookings(selectedEvent.event_id);
 }
 fetchEvents(); // Refresh to update counts
 } else {
 const error = await response.json();
 toast.error(error.detail || 'Failed to cancel booking');
 }
 } catch (error) {
 toast.error('Failed to cancel booking');
 } finally {
 setSaving(false);
 setCancelBookingConfirm(null);
 }
 };

 // testEmailConfiguration function and testEmailAddress state removed - now in TestEmailDialog component

 const filteredEvents = events.filter(event => 
 !searchQuery || 
 event.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
 event.venue_name?.toLowerCase().includes(searchQuery.toLowerCase())
 );

 if (loading) {
 return (
 <div className="flex items-center justify-center py-12">
 <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
 </div>
 );
 }

 // Show Weekender Workspace if an event is selected
 if (workspaceEvent) {
 return (
 <WeekenderWorkspace
 event={workspaceEvent}
 authToken={authToken}
 onBack={() => {
 setWorkspaceEvent(null);
 fetchEvents(); // Refresh events list
 }}
 onEventUpdated={fetchEvents}
 />
 );
 }

 return (
 <div className="space-y-6">
 {/* Header */}
 <div className="flex items-center justify-between">
 <div>
 <h3 className="text-lg font-semibold text-slate-900">Weekend Dance Events</h3>
 <p className="text-sm text-slate-500">Manage line dance weekend holidays and bookings</p>
 </div>
 <div className="flex items-center gap-2">
 {/* Test Email button moved to Email Templates tab (Phase 3J) */}
 <Button 
 variant="outline"
 size="sm"
 onClick={fetchPaymentPlans}
 data-testid="view-payment-plans-btn"
 title="View all payment plans"
 >
 <RefreshCw className="w-4 h-4 mr-2" />
 Payment Plans
 </Button>
 {!isBookingManager && (
 <Button 
 onClick={() => {
 setEditingEvent(null);
 setShowEventDialog(true);
 }}
 data-testid="add-weekend-event-btn"
 >
 <Plus className="w-4 h-4 mr-2" />
 Add Weekend Event
 </Button>
 )}
 </div>
 </div>

 {/* Events List */}
 {filteredEvents.length === 0 ? (
 <div className="text-center py-12 bg-slate-50 rounded-xl">
 <Calendar className="w-12 h-12 mx-auto text-slate-500 mb-3" />
 <p className="text-slate-600">No weekend events yet</p>
 <p className="text-sm text-slate-500">Create your first event to get started</p>
 </div>
 ) : (
 <div className="grid gap-4">
 {filteredEvents.map(event => (
 <div 
 key={event.event_id}
 className="bg-white border border-slate-200 rounded-xl p-4 hover:shadow-md transition-shadow"
 data-testid={`weekend-event-${event.event_id}`}
 >
 <div className="flex items-start gap-4">
 {/* Image */}
 <div className="w-24 h-24 bg-slate-100 rounded-lg overflow-hidden flex-shrink-0">
 {event.image_url ? (
 <img src={event.image_url} alt={event.title} className="w-full h-full object-cover" />
 ) : (
 <div className="w-full h-full flex items-center justify-center">
 <Calendar className="w-8 h-8 text-slate-500" />
 </div>
 )}
 </div>

 {/* Details */}
 <div className="flex-1 min-w-0">
 <div className="flex items-center gap-2 mb-1">
 <h4 className="font-semibold text-slate-900 truncate">{event.title}</h4>
 <Badge variant={event.is_published ?'default' :'secondary'}>
 {event.is_published ?'Published' :'Draft'}
 </Badge>
 </div>
 
 <div className="space-y-1 text-sm text-slate-600">
 <div className="flex items-center gap-2">
 <MapPin className="w-4 h-4 text-slate-500" />
 <span>{event.venue_name}</span>
 </div>
 <div className="flex items-center gap-2">
 <Calendar className="w-4 h-4 text-slate-500" />
 <span>{formatDate(event.start_date)} - {formatDate(event.end_date)}</span>
 </div>
 <div className="flex items-center gap-4">
 <span className="flex items-center gap-1">
 <Users className="w-4 h-4 text-slate-500" />
 {event.current_bookings || 0} bookings
 </span>
 {event.room_options?.length > 0 && (
 <span className="flex items-center gap-1">
 <PoundSterling className="w-4 h-4 text-slate-500" />
 From £{Math.min(...event.room_options.map(r => r.price_per_person))} pp
 </span>
 )}
 </div>
 </div>
 </div>

 {/* Actions */}
 <div className="flex items-center gap-2">
 <Button 
 variant="ghost" 
 size="sm"
 onClick={() => togglePublished(event)}
 title={event.is_published ?'Unpublish' :'Publish'}
 >
 {event.is_published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
 </Button>
 <Button 
 variant="default" 
 size="sm"
 onClick={() => setWorkspaceEvent(event)}
 title="Manage Weekender"
 className="bg-violet-600 hover:bg-violet-700 text-white"
 data-testid={`manage-event-${event.event_id}`}
 >
 <LayoutDashboard className="w-4 h-4 mr-1" />
 Manage
 </Button>
                {/* Legacy buttons hidden - functionality moved to Workspace
                    View bookings → Workspace > Bookings
                    View summary → Workspace > Overview
                    Edit → Workspace > Settings
                    Delete → Workspace > Settings > Danger Zone
                */}
 </div>
 </div>
 </div>
 ))}
 </div>
 )}

 {/* Create/Edit Event Dialog - Using extracted component */}
 <CreateWeekenderDialog
 open={showEventDialog}
 onClose={() => setShowEventDialog(false)}
 event={editingEvent}
 authToken={localStorage.getItem('token')}
 onSuccess={fetchEvents}
 />


 {/* Legacy Bookings Dialog removed in Phase 3F - functionality moved to WeekenderBookings tab */}

 {/* Edit Booking Dialog - Using extracted component */}
 <EditWeekenderBookingDialog
 booking={selectedBooking}
 event={selectedEvent}
 open={showEditBookingDialog}
 onClose={() => setShowEditBookingDialog(false)}
 authToken={localStorage.getItem('token')}
 onSuccess={handleBookingOperationSuccess}
 />

 {/* Record Payment Dialog - Using extracted component */}
 <RecordWeekenderPaymentDialog
 booking={selectedBooking}
 open={showPaymentDialog}
 onClose={() => setShowPaymentDialog(false)}
 authToken={localStorage.getItem('token')}
 onSuccess={handleBookingOperationSuccess}
 />

 {/* Invoice Dialog - Using extracted component */}
 <WeekenderInvoiceDialog
 booking={selectedBooking}
 open={showInvoiceDialog}
 onClose={() => setShowInvoiceDialog(false)}
 authToken={localStorage.getItem('token')}
 />

 {/* Delete Booking Confirmation */}
 <AlertDialog open={!!deleteBookingConfirm} onOpenChange={() => setDeleteBookingConfirm(null)}>
 <AlertDialogContent className="bg-white">
 <AlertDialogHeader>
 <AlertDialogTitle className="text-slate-900">Delete Booking?</AlertDialogTitle>
 <AlertDialogDescription className="text-slate-600">
 Are you sure you want to delete the booking for <strong>{deleteBookingConfirm?.customer_name}</strong>? 
 This will restore the room availability. This action cannot be undone.
 </AlertDialogDescription>
 </AlertDialogHeader>
 <AlertDialogFooter>
 <AlertDialogCancel>Cancel</AlertDialogCancel>
 <AlertDialogAction 
 onClick={() => handleDeleteBooking(deleteBookingConfirm?.booking_id)}
 className="bg-red-600 hover:bg-red-700"
 >
 Delete Booking
 </AlertDialogAction>
 </AlertDialogFooter>
 </AlertDialogContent>
 </AlertDialog>

 {/* Cancel Booking Confirmation */}
 <AlertDialog open={!!cancelBookingConfirm} onOpenChange={() => setCancelBookingConfirm(null)}>
 <AlertDialogContent className="bg-white">
 <AlertDialogHeader>
 <AlertDialogTitle className="text-slate-900">Cancel Booking?</AlertDialogTitle>
 <AlertDialogDescription className="text-slate-600">
 Are you sure you want to cancel the booking for <strong>{cancelBookingConfirm?.customer_name}</strong>?
 <br /><br />
 This will:
 <ul className="list-disc ml-5 mt-2 space-y-1">
 <li>Set the booking status to "Cancelled"</li>
 <li>Restore room availability</li>
 <li>Cancel any active payment plans</li>
 <li>Send a cancellation email to the customer</li>
 </ul>
 <br />
 {cancelBookingConfirm?.amount_paid > 0 && (
 <span className="text-orange-600 font-medium">
 Note: Customer has paid £{cancelBookingConfirm?.amount_paid?.toFixed(2)}. You'll need to process refunds separately.
 </span>
 )}
 </AlertDialogDescription>
 </AlertDialogHeader>
 <AlertDialogFooter>
 <AlertDialogCancel>Go Back</AlertDialogCancel>
 <Button
 variant="outline"
 onClick={() => handleCancelBooking(cancelBookingConfirm?.booking_id, false)}
 disabled={saving}
 >
 Cancel Without Email
 </Button>
 <AlertDialogAction 
 onClick={() => handleCancelBooking(cancelBookingConfirm?.booking_id, true)}
 className="bg-orange-600 hover:bg-orange-700"
 disabled={saving}
 >
 {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
 Cancel & Notify Customer
 </AlertDialogAction>
 </AlertDialogFooter>
 </AlertDialogContent>
 </AlertDialog>

 {/* Test Email Dialog removed - moved to Email Templates tab (Phase 3J) */}

 {/* Payment Plans Dialog */}
 <Dialog open={showPaymentPlansDialog} onOpenChange={setShowPaymentPlansDialog}>
 <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto bg-white">
 <DialogHeader>
 <DialogTitle className="text-slate-900">Payment Plans & Dancer Shares</DialogTitle>
 <DialogDescription className="text-slate-600">
 Manage recurring payment plans and dancer payment shares for weekend bookings
 </DialogDescription>
 </DialogHeader>
 <div className="space-y-4 mt-4">
 {paymentPlans.length === 0 ? (
 <div className="text-center py-8 text-slate-500">
 <RefreshCw className="w-8 h-8 mx-auto mb-2 opacity-50" />
 <p className="font-medium text-slate-700">No payment plans found</p>
 <p className="text-sm mt-2 max-w-md mx-auto">
 Payment plans are created when customers choose the"Deposit + Monthly Payments" option during checkout. 
 Dancer shares appear when booking leads split payments with their group.
 </p>
 </div>
 ) : (
 <div className="space-y-3">
 {paymentPlans.map(plan => (
 <div 
 key={plan.plan_id}
 className={`p-4 rounded-lg border ${
 plan.type ==='dancer_share' ? (
 plan.status ==='active' ?'border-purple-300 bg-purple-50' :
 plan.status ==='completed' ?'border-purple-300 bg-purple-100' :
'border-purple-200 bg-purple-50/50'
 ) : (
 plan.status ==='active' ?'border-green-300 bg-green-50' :
 plan.status ==='paused' ?'border-yellow-300 bg-yellow-50' :
 plan.status ==='completed' ?'border-blue-300 bg-blue-50' :
'border-slate-200 bg-slate-50'
 )
 }`}
 >
 <div className="flex items-start justify-between">
 <div className="space-y-1">
 <div className="flex items-center gap-2">
 <span className="font-medium text-slate-900">{plan.booking_id}</span>
 {plan.type ==='dancer_share' && (
 <Badge className="bg-purple-200 text-purple-800 border border-purple-300">
 👤 Dancer Share
 </Badge>
 )}
 <Badge className={
 plan.status ==='active' ?'bg-green-200 text-green-800 border border-green-300' :
 plan.status ==='paused' ?'bg-yellow-200 text-yellow-800 border border-yellow-300' :
 plan.status ==='completed' ?'bg-blue-200 text-blue-800 border border-blue-300' :
'bg-slate-200 text-slate-800 border border-slate-300'
 }>
 {plan.status}
 </Badge>
 </div>
 <p className="text-sm text-slate-700">{plan.customer_name} ({plan.customer_email})</p>
 <div className="text-sm text-slate-600 grid grid-cols-2 gap-x-4 gap-y-1 mt-2">
 <span>Monthly: <strong className="text-slate-900">£{plan.monthly_amount?.toFixed(2)}</strong></span>
 <span>Remaining: <strong className="text-slate-900">£{plan.remaining_balance?.toFixed(2)}</strong></span>
 {plan.type ==='dancer_share' && (
 plan.has_card ? (
 <span className="text-green-700">Card: {plan.card_brand} ****{plan.card_last4}</span>
 ) : (
 <span className="text-red-600 font-medium">⚠ No card on file</span>
 )
 )}
 {plan.type !=='dancer_share' && plan.card_last4 && (
 <span className="text-slate-700">Card: {plan.card_brand} ****{plan.card_last4}</span>
 )}
 <span className="text-slate-700">Payments: {plan.payments_made || 0}</span>
 {plan.type ==='dancer_share' && (
 <span className="col-span-2 text-slate-700">
 Share Total: <strong className="text-slate-900">£{plan.booking_total?.toFixed(2)}</strong> • 
 Paid: <strong className="text-slate-900">£{plan.booking_amount_paid?.toFixed(2)}</strong>
 </span>
 )}
 {plan.next_payment_date && (
 <span className="col-span-2 text-slate-700">
 Next payment: {new Date(plan.next_payment_date).toLocaleDateString('en-GB', {
 day:'numeric', month:'short', year:'numeric'
 })}
 </span>
 )}
 {plan.type !=='dancer_share' && (
 <span className="col-span-2 flex items-center gap-2 text-slate-700">
 End date: 
 <input
 type="date"
 value={plan.end_date ? new Date(plan.end_date).toISOString().split('T')[0] :''}
 onChange={(e) => handleSetEndDate(plan.plan_id, e.target.value)}
 className="border border-slate-300 rounded px-2 py-1 text-xs bg-white text-slate-900"
 min={new Date().toISOString().split('T')[0]}
 />
 {plan.end_date && (
 <button 
 onClick={() => handleSetEndDate(plan.plan_id, null)}
 className="text-red-600 hover:text-red-700 text-xs font-medium"
 >
 Clear
 </button>
 )}
 </span>
 )}
 {plan.last_payment_error && (
 <span className="col-span-2 text-red-700 font-medium">
 Error: {plan.last_payment_error}
 </span>
 )}
 </div>
 </div>
 {/* Actions Dropdown Menu */}
 <div className="flex items-center gap-2">
 {/* Status Badge */}
 <Badge className={
 plan.status ==='active' ?'bg-green-100 text-green-800' :
 plan.status ==='paused' ?'bg-amber-100 text-amber-800' :
 plan.status ==='pending' ?'bg-blue-100 text-blue-800' :
 plan.status ==='completed' ?'bg-slate-100 text-slate-800' :
'bg-red-100 text-red-800'
 }>
 {plan.status}
 </Badge>
 
 <DropdownMenu>
 <DropdownMenuTrigger asChild>
 <Button variant="outline" size="sm">
 <MoreHorizontal className="w-4 h-4" />
 <span className="ml-1 hidden sm:inline">Actions</span>
 </Button>
 </DropdownMenuTrigger>
 <DropdownMenuContent align="end" className="w-48">
 {/* Dancer Share actions */}
 {plan.type ==='dancer_share' && (plan.status ==='active' || plan.status ==='paused' || plan.status ==='pending') && (
 <>
 <DropdownMenuItem onClick={() => handleEditDancerShare(plan)}>
 <Edit className="w-4 h-4 mr-2" />
 Edit Details
 </DropdownMenuItem>
 
 {plan.status ==='active' && (
 <DropdownMenuItem onClick={() => handlePauseResumeDancerShare(plan.plan_id, plan.status)}>
 <Pause className="w-4 h-4 mr-2" />
 Pause
 </DropdownMenuItem>
 )}
 {(plan.status ==='paused' || plan.status ==='pending') && (
 <DropdownMenuItem onClick={() => handlePauseResumeDancerShare(plan.plan_id, plan.status)}>
 <Play className="w-4 h-4 mr-2" />
 {plan.status ==='pending' ?'Activate' :'Resume'}
 </DropdownMenuItem>
 )}
 
 <DropdownMenuSeparator />
 
 <DropdownMenuItem onClick={() => setProcessConfirmPlan(plan)} className="text-purple-600">
 <CreditCard className="w-4 h-4 mr-2" />
 Process Payment
 </DropdownMenuItem>
 
 <DropdownMenuItem onClick={() => handleResendPaymentLink(plan.plan_id, plan.customer_email)}>
 <Send className="w-4 h-4 mr-2" />
 Resend Link
 </DropdownMenuItem>
 
 {!plan.has_card && (
 <DropdownMenuItem onClick={() => handleOpenAddCard(plan)} className="text-orange-600">
 <CreditCard className="w-4 h-4 mr-2" />
 Add Card
 </DropdownMenuItem>
 )}
 {plan.has_card && (
 <DropdownMenuItem onClick={() => handleOpenAddCard(plan)}>
 <CreditCard className="w-4 h-4 mr-2" />
 Update Card
 </DropdownMenuItem>
 )}
 </>
 )}
 
 {/* Regular Payment Plan actions */}
 {plan.type !=='dancer_share' && (plan.status ==='active' || plan.status ==='paused') && (
 <>
 <DropdownMenuItem onClick={() => handleEditPaymentPlan(plan)}>
 <Edit className="w-4 h-4 mr-2" />
 Edit Details
 </DropdownMenuItem>
 
 {plan.status ==='active' && (
 <DropdownMenuItem onClick={() => handlePauseResumePlan(plan.plan_id, plan.status)}>
 <Pause className="w-4 h-4 mr-2" />
 Pause
 </DropdownMenuItem>
 )}
 {plan.status ==='paused' && (
 <DropdownMenuItem onClick={() => handlePauseResumePlan(plan.plan_id, plan.status)}>
 <Play className="w-4 h-4 mr-2" />
 Resume
 </DropdownMenuItem>
 )}
 
 <DropdownMenuSeparator />
 
 <DropdownMenuItem onClick={() => setProcessConfirmPlan(plan)} className="text-green-600">
 <CreditCard className="w-4 h-4 mr-2" />
 Process Payment
 </DropdownMenuItem>
 
 <DropdownMenuSeparator />
 
 <DropdownMenuItem onClick={() => handleCancelPlan(plan.plan_id)} className="text-red-600">
 <X className="w-4 h-4 mr-2" />
 Cancel Plan
 </DropdownMenuItem>
 </>
 )}
 
 {/* Delete option for cancelled/completed plans */}
 {(plan.status ==='cancelled' || plan.status ==='completed') && (
 <DropdownMenuItem 
 onClick={() => handleDeleteCancelledPlan(plan.plan_id, plan.type)} 
 className="text-red-600"
 disabled={deletingPlanId === plan.plan_id}
 >
 {deletingPlanId === plan.plan_id ? (
 <Loader2 className="w-4 h-4 mr-2 animate-spin" />
 ) : (
 <Trash2 className="w-4 h-4 mr-2" />
 )}
 Delete Plan
 </DropdownMenuItem>
 )}
 </DropdownMenuContent>
 </DropdownMenu>
 </div>
 </div>
 </div>
 ))}
 </div>
 )}
 </div>
 </DialogContent>
 </Dialog>

 {/* Edit Dancer Share Dialog */}
 <Dialog open={!!editingDancerShare} onOpenChange={(open) => !open && setEditingDancerShare(null)}>
 <DialogContent className="max-w-md">
 <DialogHeader>
 <DialogTitle className="flex items-center gap-2">
 <Edit className="w-5 h-5 text-purple-600" />
 Edit Dancer Share
 </DialogTitle>
 <DialogDescription>
 Update payment details for this dancer share
 </DialogDescription>
 </DialogHeader>
 <div className="space-y-4 mt-4">
 <div>
 <label className="block text-sm font-medium text-slate-700 mb-1">Dancer Name</label>
 <Input
 value={editDancerShareForm.dancer_name}
 onChange={(e) => setEditDancerShareForm(prev => ({ ...prev, dancer_name: e.target.value }))}
 placeholder="Dancer name"
 />
 </div>
 <div>
 <label className="block text-sm font-medium text-slate-700 mb-1">Dancer Email</label>
 <Input
 type="email"
 value={editDancerShareForm.dancer_email}
 onChange={(e) => setEditDancerShareForm(prev => ({ ...prev, dancer_email: e.target.value }))}
 placeholder="dancer@email.com"
 />
 </div>
 <div>
 <label className="block text-sm font-medium text-slate-700 mb-1">Total Share Amount (£)</label>
 <Input
 type="number"
 step="0.01"
 value={editDancerShareForm.share_amount}
 onChange={(e) => setEditDancerShareForm(prev => ({ ...prev, share_amount: e.target.value }))}
 placeholder="0.00"
 />
 </div>
 <div>
 <label className="block text-sm font-medium text-slate-700 mb-1">Monthly Payment (£)</label>
 <Input
 type="number"
 step="0.01"
 value={editDancerShareForm.monthly_payment}
 onChange={(e) => setEditDancerShareForm(prev => ({ ...prev, monthly_payment: e.target.value }))}
 placeholder="0.00"
 />
 </div>
 </div>
 <div className="flex justify-end gap-2 mt-6">
 <Button variant="outline" onClick={() => setEditingDancerShare(null)}>
 Cancel
 </Button>
 <Button 
 onClick={handleSaveDancerShare}
 className="bg-purple-600 hover:bg-purple-700"
 >
 Save Changes
 </Button>
 </div>
 </DialogContent>
 </Dialog>

 {/* Edit Payment Plan Dialog */}
 <Dialog open={!!editingPaymentPlan} onOpenChange={(open) => !open && setEditingPaymentPlan(null)}>
 <DialogContent className="max-w-md">
 <DialogHeader>
 <DialogTitle className="flex items-center gap-2">
 <Edit className="w-5 h-5 text-green-600" />
 Edit Payment Plan
 </DialogTitle>
 <DialogDescription>
 Update payment details for this plan
 </DialogDescription>
 </DialogHeader>
 <div className="space-y-4 mt-4">
 <div>
 <label className="block text-sm font-medium text-slate-700 mb-1">Monthly Payment (£)</label>
 <Input
 type="number"
 step="0.01"
 value={editPaymentPlanForm.monthly_amount}
 onChange={(e) => setEditPaymentPlanForm(prev => ({ ...prev, monthly_amount: e.target.value }))}
 placeholder="0.00"
 />
 </div>
 <div>
 <label className="block text-sm font-medium text-slate-700 mb-1">Remaining Balance (£)</label>
 <Input
 type="number"
 step="0.01"
 value={editPaymentPlanForm.remaining_balance}
 onChange={(e) => setEditPaymentPlanForm(prev => ({ ...prev, remaining_balance: e.target.value }))}
 placeholder="0.00"
 />
 <p className="text-xs text-slate-500 mt-1">Adjust if manual payments were made outside the system</p>
 </div>
 </div>
 <div className="flex justify-end gap-2 mt-6">
 <Button variant="outline" onClick={() => setEditingPaymentPlan(null)}>
 Cancel
 </Button>
 <Button 
 onClick={handleSavePaymentPlan}
 className="bg-green-600 hover:bg-green-700"
 >
 Save Changes
 </Button>
 </div>
 </DialogContent>
 </Dialog>

 {/* Add/Update Card for Dancer Share Dialog */}
 <Dialog open={!!addingCardForShare} onOpenChange={(open) => {
 if (!open) {
 setAddingCardForShare(null);
 setSquareCard(null);
 }
 }}>
 <DialogContent className="max-w-md">
 <DialogHeader>
 <DialogTitle className="flex items-center gap-2">
 <CreditCard className="w-5 h-5 text-orange-600" />
 {addingCardForShare?.has_card ?'Update Card' :'Add Card'} for Dancer
 </DialogTitle>
 <DialogDescription>
 {addingCardForShare?.has_card 
 ? `Replace the existing card for ${addingCardForShare?.customer_name}`
 : `Manually add a card on file for ${addingCardForShare?.customer_name}`
 }
 </DialogDescription>
 </DialogHeader>
 <div className="space-y-4 mt-4">
 <div className="bg-slate-50 p-3 rounded-lg text-sm">
 <p><strong>Dancer:</strong> {addingCardForShare?.customer_name}</p>
 <p><strong>Email:</strong> {addingCardForShare?.customer_email}</p>
 <p><strong>Share Amount:</strong> £{addingCardForShare?.booking_total?.toFixed(2)}</p>
 {addingCardForShare?.has_card && (
 <p className="text-orange-600 mt-1"><strong>Current Card:</strong> {addingCardForShare?.card_brand} ****{addingCardForShare?.card_last4}</p>
 )}
 </div>
 <div>
 <label className="block text-sm font-medium text-slate-700 mb-2">Card Details</label>
 <div 
 id="admin-card-container" 
 className="border border-slate-300 rounded-lg p-3 min-h-[50px] bg-white"
 />
 {!squareCard && squareLoaded && (
 <p className="text-xs text-slate-500 mt-1">Loading card form...</p>
 )}
 </div>
 <p className="text-xs text-slate-500">
 Enter the dancer's card details. This card will be saved for automatic monthly payments.
 </p>
 </div>
 <div className="flex justify-end gap-2 mt-6">
 <Button 
 variant="outline" 
 onClick={() => {
 setAddingCardForShare(null);
 setSquareCard(null);
 }}
 >
 Cancel
 </Button>
 <Button 
 onClick={handleAddCardForShare}
 disabled={!squareCard || addingCardProcessing}
 className="bg-orange-600 hover:bg-orange-700"
 >
 {addingCardProcessing ? (
 <>
 <Loader2 className="w-4 h-4 mr-2 animate-spin" />
 Adding...
 </>
 ) : (
'Add Card'
 )}
 </Button>
 </div>
 </DialogContent>
 </Dialog>

 {/* Legacy Summary Dialog and Set Room Counts Dialog removed in Phase 3K */}
 {/* Summary view is now in WeekenderOverview with SummaryPrintExport */}
 {/* Set Room Counts (manual override) is now in WeekenderRooms */}

 {/* Process Payment Confirmation Dialog */}
 <AlertDialog open={!!processConfirmPlan} onOpenChange={(open) => !open && setProcessConfirmPlan(null)}>
 <AlertDialogContent>
 <AlertDialogHeader>
 <AlertDialogTitle className="flex items-center gap-2">
 <CreditCard className="w-5 h-5 text-green-600" />
 Confirm Payment
 </AlertDialogTitle>
 <AlertDialogDescription className="space-y-3">
 <p>You are about to process a payment for:</p>
 <div className="bg-slate-50 rounded-lg p-4 space-y-2">
 <div className="flex justify-between">
 <span className="text-slate-600">Customer:</span>
 <span className="font-medium text-slate-900">
 {processConfirmPlan?.customer_name || processConfirmPlan?.dancer_name ||'Unknown'}
 </span>
 </div>
 <div className="flex justify-between">
 <span className="text-slate-600">Type:</span>
 <span className="font-medium text-slate-900">
 {processConfirmPlan?.type ==='dancer_share' ?'Dancer Share' :'Payment Plan'}
 </span>
 </div>
 <div className="flex justify-between border-t pt-2 mt-2">
 <span className="text-slate-600 font-semibold">Amount to charge:</span>
 <span className="font-bold text-lg text-green-600">
 £{(() => {
 if (!processConfirmPlan) return'0.00';
 const monthlyAmount = processConfirmPlan.monthly_amount || processConfirmPlan.monthly_payment || 0;
 const remainingBalance = processConfirmPlan.remaining_balance || processConfirmPlan.booking_total || 0;
 const chargeAmount = Math.min(monthlyAmount, remainingBalance);
 return chargeAmount.toFixed(2);
 })()}
 </span>
 </div>
 <div className="flex justify-between text-sm">
 <span className="text-slate-500">Remaining after payment:</span>
 <span className="text-slate-700">
 £{(() => {
 if (!processConfirmPlan) return'0.00';
 const monthlyAmount = processConfirmPlan.monthly_amount || processConfirmPlan.monthly_payment || 0;
 const remainingBalance = processConfirmPlan.remaining_balance || processConfirmPlan.booking_total || 0;
 const chargeAmount = Math.min(monthlyAmount, remainingBalance);
 const newBalance = Math.max(0, remainingBalance - chargeAmount);
 return newBalance.toFixed(2);
 })()}
 </span>
 </div>
 </div>
 {!processConfirmPlan?.has_card && !processConfirmPlan?.card_id && (
 <p className="text-amber-600 text-sm flex items-center gap-1">
 <AlertCircle className="w-4 h-4" />
 Warning: No card on file - payment may fail
 </p>
 )}
 </AlertDialogDescription>
 </AlertDialogHeader>
 <AlertDialogFooter>
 <AlertDialogCancel>Cancel</AlertDialogCancel>
 <AlertDialogAction
 onClick={() => {
 if (processConfirmPlan?.type ==='dancer_share') {
 handleProcessDancerShare(processConfirmPlan.plan_id);
 } else {
 handleProcessSinglePlan(processConfirmPlan.plan_id);
 }
 }}
 className="bg-green-600 hover:bg-green-700"
 >
 <CreditCard className="w-4 h-4 mr-2" />
 Process Payment
 </AlertDialogAction>
 </AlertDialogFooter>
 </AlertDialogContent>
 </AlertDialog>
 </div>
 );
}
